import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, isOverdue, PRIORITY, TASK_STATUS } from "../lib/labels";
import { userName, useUsers } from "../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../components/common";
import TaskDialog from "../components/TaskDialog";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";

const VIEWS = [
  { id: "atencion", label: "Requiere mi atención" },
  { id: "vencido", label: "Vencido" },
  { id: "hoy", label: "Hoy" },
  { id: "semana", label: "Esta semana" },
  { id: "esperando", label: "Esperando de terceros" },
  { id: "revision", label: "Esperando mi revisión" },
  { id: "completadas", label: "Completado recientemente" },
];

function NewTaskDialog({ open, onClose, onCreated }) {
  const { users } = useUsers();
  const { data: exps } = useQuery({ queryKey: ["expedientes-mini"], queryFn: async () => (await api.get("/expedientes")).data });
  const [form, setForm] = useState({ title: "", description: "", assignee_id: "", expediente_id: "", due_date: "", priority: "media" });
  const submit = async () => {
    try {
      await api.post("/tasks", { ...form, assignee_id: form.assignee_id || undefined, expediente_id: form.expediente_id || undefined, due_date: form.due_date ? new Date(form.due_date).toISOString() : undefined });
      toast.success("Tarea creada");
      onClose(); onCreated?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-task-dialog">
        <DialogHeader><DialogTitle>Nueva tarea</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Título *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-task-title" /></div>
          <div><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-task-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Responsable</Label>
              <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                <SelectTrigger data-testid="new-task-assignee"><SelectValue placeholder="Yo" /></SelectTrigger>
                <SelectContent>{users.filter((u) => !u.roles.includes("VIEWER")).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expediente</Label>
              <Select value={form.expediente_id} onValueChange={(v) => setForm({ ...form, expediente_id: v })}>
                <SelectTrigger data-testid="new-task-expediente"><SelectValue placeholder="Sin expediente" /></SelectTrigger>
                <SelectContent>{(exps || []).slice(0, 50).map((e) => <SelectItem key={e.id} value={e.id}>{e.code} — {e.title.slice(0, 40)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fecha límite</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="new-task-due" /></div>
            <div>
              <Label>Prioridad</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="new-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={submit} disabled={!form.title.trim()} className="w-full" data-testid="new-task-submit">Crear tarea</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MiTrabajo() {
  const [params, setParams] = useSearchParams();
  const vista = params.get("vista") || "atencion";
  const [filters, setFilters] = useState({ area: "", priority: "", q: "" });
  const [taskId, setTaskId] = useState(null);
  const [newOpen, setNewOpen] = useState(params.get("nueva") === "1");
  const { map } = useUsers();
  const qc = useQueryClient();

  useEffect(() => { if (params.get("nueva") === "1") setNewOpen(true); }, [params]);

  const { data: tasks, refetch, isLoading } = useQuery({
    queryKey: ["tasks", vista, filters],
    queryFn: async () => (await api.get("/tasks", { params: { view: vista, area: filters.area || undefined, priority: filters.priority || undefined, q: filters.q || undefined } })).data,
  });

  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["dashboard-badges"] }); };

  return (
    <div data-testid="mi-trabajo-page">
      <PageHeader
        title="Mi trabajo"
        subtitle="Tus tareas, revisiones y aprobaciones. Los cierres ejecutan los controles del proceso en el servidor."
        actions={<Button onClick={() => setNewOpen(true)} data-testid="new-task-button"><Plus className="mr-1 h-4 w-4" /> Nueva tarea</Button>}
      />

      <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            role="tab"
            aria-selected={vista === v.id}
            onClick={() => setParams({ vista: v.id })}
            data-testid={`task-view-${v.id}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${vista === v.id ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Buscar tarea…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="h-8 w-56" data-testid="task-search-input" />
        <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v === "todas" ? "" : v })}>
          <SelectTrigger className="h-8 w-36" data-testid="task-priority-filter"><SelectValue placeholder="Prioridad" /></SelectTrigger>
          <SelectContent><SelectItem value="todas">Todas</SelectItem>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando tareas…</p>
      ) : !tasks?.length ? (
        <EmptyState title="Nada por aquí" description="No hay tareas en esta vista con los filtros actuales." testid="tasks-empty" />
      ) : (
        <div className="ts-card overflow-hidden">
          <Table data-testid="tasks-table">
            <TableHeader>
              <TableRow>
                <TableHead>Tarea</TableHead>
                <TableHead className="hidden md:table-cell">Área</TableHead>
                <TableHead className="hidden lg:table-cell">Responsable</TableHead>
                <TableHead>Vencimiento</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="hidden sm:table-cell">Controles</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map((t) => (
                <TableRow key={t.id} className="ts-table-row" onClick={() => setTaskId(t.id)} data-testid={`task-row-${t.id}`}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{t.title}</p>
                    <p className="text-xs text-slate-400">{t.expediente_id ? "Vinculada a expediente" : t.project_id ? "Vinculada a proyecto" : "Tarea libre"} · <Chip map={PRIORITY} value={t.priority} /></p>
                  </TableCell>
                  <TableCell className="hidden text-sm text-slate-600 md:table-cell">{t.area}</TableCell>
                  <TableCell className="hidden text-sm text-slate-600 lg:table-cell">{userName(map, t.assignee_id)}</TableCell>
                  <TableCell className={`text-sm ${isOverdue(t.due_date) && !["completada", "cancelada"].includes(t.status) ? "font-semibold text-red-600" : "text-slate-600"}`}>
                    {fmtDate(t.due_date)}
                  </TableCell>
                  <TableCell><Chip map={TASK_STATUS} value={t.status} testid={`task-status-${t.id}`} /></TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div className="flex gap-1">
                      {t.requires_evidence && <span className="ts-chip ts-chip-warning" title="Requiere evidencia">E</span>}
                      {t.requires_review && <span className="ts-chip ts-chip-ai" title="Requiere revisión">R</span>}
                      {t.blockers?.length > 0 && <span className="ts-chip ts-chip-danger" title={t.blockers.join("; ")}>B</span>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <TaskDialog taskId={taskId} open={!!taskId} onClose={() => setTaskId(null)} onChanged={refresh} />
      <NewTaskDialog open={newOpen} onClose={() => { setNewOpen(false); params.delete("nueva"); setParams(params); }} onCreated={refresh} />

      <ReunionesTrabajo />
    </div>
  );
}

const MW_GROUPS = [
  ["ejecutar", "Ejecutar acción"], ["aportar_evidencia", "Aportar evidencia"], ["revisar_evidencia", "Revisar evidencia"],
  ["verificar_decision", "Verificar decisión"], ["responder_cuestion", "Responder cuestión"], ["validar_respuesta", "Validar respuesta"],
  ["revisar_compromiso", "Revisar compromiso contractual"], ["resolver_bloqueo", "Resolver bloqueo"], ["preparar_reunion", "Preparar siguiente reunión"],
];

function ReunionesTrabajo() {
  const { user } = useAuth();
  const external = (user?.roles || []).includes("VIEWER");
  const { data } = useQuery({ queryKey: ["reuniones-mi-trabajo"], queryFn: async () => (await api.get("/reuniones/seguimiento/mi-trabajo")).data });
  if (!data) return null;
  const total = MW_GROUPS.reduce((n, [k]) => n + (data[k]?.length || 0), 0);
  return (
    <section className="mt-6" data-testid="reuniones-trabajo">
      <h2 className="ts-section-title mb-2">Reuniones y actas ({total})</h2>
      {total === 0 ? <p className="text-xs text-slate-400">Nada pendiente de reuniones.</p> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {MW_GROUPS.map(([key, label]) => (data[key] || []).length ? (
            <div key={key} className="ts-card p-3" data-testid={`mw-${key}`}>
              <p className="ts-caption mb-1">{label} ({data[key].length})</p>
              <ul className="divide-y divide-slate-100">
                {data[key].slice(0, 4).map((it) => (
                  <li key={it.id}>
                    <a href={external ? "/mi-trabajo?vista=atencion" : `/reuniones/${it.meeting_id || it.id}/seguimiento`} className="block truncate py-1.5 text-xs text-slate-700 hover:text-blue-700" data-testid={`mw-item-${it.id}`}>
                      {it.title || it.question || it.text || it.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null)}
        </div>
      )}
    </section>
  );
}
